/**
 * MoreWaysToJoin - Expandable panel for Inner Circle & Special Events
 * 
 * Design principles:
 * - Calm, not salesy
 * - Context-aware (respects verification state)
 * - Optional expansion, no auto-open
 * - Equal visual weight for options
 */

import React, { useState, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../App';
import { trackEvent } from '../../api/analytics';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';
import InlineNotice from '../common/InlineNotice';

/**
 * @param {Object} props
 * @param {boolean} [props.isVerified] - Whether user is identity verified
 * @param {boolean} [props.isInnerCircle] - Whether user is Inner Circle member
 */
export default function MoreWaysToJoin({ isVerified = false, isInnerCircle = false }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  const handleToggle = useCallback(() => {
    const wasExpanded = expanded;
    setExpanded(prev => !prev);
    
    // Track analytics - both expand and collapse
    if (!wasExpanded) {
      trackEvent('more_ways_to_join_expanded', { from: 'speed_dating_lobby' });
    } else {
      trackEvent('more_ways_to_join_collapsed', { from: 'speed_dating_lobby' });
    }
  }, [expanded]);

  const handleInnerCircleClick = useCallback(() => {
    trackEvent('inner_circle_cta_clicked', { 
      from: 'speed_dating_lobby',
      is_verified: isVerified,
      is_member: isInnerCircle
    });
    
    if (!isVerified) {
      // Redirect to verify first
      const returnUrl = encodeURIComponent(location.pathname);
      navigate(`/verify-age?return=${returnUrl}`);
      return;
    }
    
    if (isInnerCircle) {
      // Already a member - go to manage
      navigate('/settings/membership');
      return;
    }
    
    // Go to Inner Circle signup
    navigate('/shop?plan=inner-circle');
  }, [isVerified, isInnerCircle, navigate, location.pathname]);

  const handleSpecialEventsClick = useCallback(() => {
    trackEvent('special_events_cta_clicked', { 
      from: 'speed_dating_lobby',
      is_verified: isVerified
    });
    
    if (!isVerified) {
      const returnUrl = encodeURIComponent(location.pathname);
      navigate(`/verify-age?return=${returnUrl}`);
      return;
    }
    
    navigate('/events/special');
  }, [isVerified, navigate, location.pathname]);

  return (
    <div className="w-full" data-testid="more-ways-to-join">
      {/* Toggle Button */}
      <button
        type="button"
        onClick={handleToggle}
        data-testid="more-ways-toggle"
        data-expanded={expanded}
        className="w-full flex items-center justify-center gap-2 py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
      >
        <span>More ways to join</span>
        {expanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      
      {!expanded && (
        <p className="text-xs text-white/40 text-center">
          Optional upgrades • No pressure
        </p>
      )}

      {/* Expanded Panel */}
      {expanded && (
        <div 
          className="mt-3 p-4 rounded-2xl border border-white/10 bg-white/5"
          data-testid="more-ways-panel"
        >
          {/* Panel Header */}
          <div className="text-center mb-4">
            <h3 className="text-base font-medium text-white/90">
              Want more time or curated events?
            </h3>
            <p className="mt-1 text-xs text-white/50">
              Daily sessions are free for verified members.
              <br />
              Inner Circle and special events offer extended time and curated experiences.
            </p>
          </div>

          {/* Option Cards */}
          <div className="space-y-3">
            {/* Inner Circle Card */}
            <div 
              className="p-4 rounded-xl border border-white/10 bg-white/5"
              data-testid="inner-circle-card"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-white/90 flex items-center gap-2">
                    Inner Circle
                    {isInnerCircle && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                        <Check className="w-3 h-3" />
                        Member
                      </span>
                    )}
                  </h4>
                  <p className="mt-1 text-xs text-white/60">
                    More time, deeper conversations, and priority matching.
                  </p>
                  
                  {/* Details */}
                  <ul className="mt-2 text-xs text-white/40 space-y-0.5">
                    <li>• Longer sessions</li>
                    <li>• Longer rounds</li>
                    <li>• Priority pairing</li>
                    <li>• Multiple sessions per day</li>
                  </ul>
                </div>
              </div>
              
              <button
                type="button"
                onClick={handleInnerCircleClick}
                disabled={isInnerCircle}
                data-testid="inner-circle-cta"
                className={`mt-3 w-full py-2.5 rounded-xl text-sm font-medium transition-all
                  ${isInnerCircle 
                    ? 'bg-white/5 text-white/40 cursor-default' 
                    : 'bg-gradient-to-r from-purple-500/80 to-pink-500/80 text-white hover:brightness-110'
                  }`}
              >
                {isInnerCircle ? 'You\'re Inner Circle' : 'Join Inner Circle'}
              </button>
              
              {!isVerified && !isInnerCircle && (
                <p className="mt-1.5 text-xs text-white/40 text-center">
                  Available to verified members
                </p>
              )}
            </div>

            {/* Special Events Card */}
            <div 
              className="p-4 rounded-xl border border-white/10 bg-white/5"
              data-testid="special-events-card"
            >
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-white/90">
                  Special Events
                </h4>
                <p className="mt-1 text-xs text-white/60">
                  Curated video speed dating with limited spots and extended rounds.
                </p>
                
                {/* Details */}
                <ul className="mt-2 text-xs text-white/40 space-y-0.5">
                  <li>• Ticketed events</li>
                  <li>• Capped attendance</li>
                  <li>• Themed nights</li>
                  <li>• Verified members only</li>
                </ul>
              </div>
              
              <button
                type="button"
                onClick={handleSpecialEventsClick}
                data-testid="special-events-cta"
                className="mt-3 w-full py-2.5 rounded-xl text-sm font-medium 
                           border border-white/20 text-white/80 
                           hover:bg-white/5 hover:text-white transition-all"
              >
                View Special Events
              </button>
              
              {!isVerified && (
                <p className="mt-1.5 text-xs text-white/40 text-center">
                  Available to verified members
                </p>
              )}
            </div>
          </div>

          {/* Footer Note */}
          <p className="mt-4 text-xs text-white/30 text-center">
            Video is private • No recording • Sessions auto-end each round
          </p>

          {/* Verification Notice */}
          {!isVerified && (
            <div className="mt-3">
              <InlineNotice
                variant="warning"
                title="A quick note"
                compact
                testId="more-ways-verify-note"
              >
                These upgrades are available to verified members. Verification keeps video + curated events safer.
              </InlineNotice>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
